<template>
  <v-dialog
    v-model="mediaUploadVisible"
    persistent
    :max-width="showVideoCrop ? 860 : 700"
  >
    <v-overlay class="center-all" :model-value="loading">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </v-overlay>
    <div class="uploadMedia">
      <v-card
        class="border-color"
        :media="media"
        v-if="!showCropDialog && !showVideoCrop"
      >
        <v-card-title class="text-center pt-4">
          <span class="text-h6 font-weight-bold text-primary">{{
            $t('setting.uploadMedia')
          }}</span>
        </v-card-title>

        <div class="d-flex justify-center pt-4 px-6">
          <div class="media-preview-wrapper">
            <my-media
              v-if="media"
              max-height="450"
              max-width="100%"
              moreClass="center-all"
              :media="media?.url"
              :mediaType="media?.mime.split('/')[0]"
              @videoTooLong="videoTooLongHandler"
            />
          </div>
        </div>

        <div v-if="videRightSize === false" class="mt-4 px-10">
          <v-alert
            type="error"
            variant="tonal"
            density="compact"
            class="rounded-xl"
          >
            {{ $t('video.tooLong') }}
          </v-alert>
        </div>
        <div v-if="exceedsVideoLimit" class="mt-4 px-10">
          <v-alert
            type="error"
            variant="tonal"
            density="compact"
            class="rounded-xl"
          >
            {{ $t('gallery.videoTooLarge') }}
          </v-alert>
        </div>

        <div class="px-10 mt-6">
          <v-text-field
            v-if="media"
            v-model="media.name"
            :error-messages="errorMassage"
            :label="$t('gallery.imageTitle')"
            variant="underlined"
            color="primary"
            class="custom-field"
            hide-details="auto"
          />
        </div>

        <div
          class="d-flex justify-center mt-6 mb-2 copyright-notice px-10 text-center"
        >
          <v-icon size="x-small" color="amber-darken-2" class="mx-1"
            >mdi-information-outline</v-icon
          >
          <span class="text-caption text-medium-emphasis font-italic">
            {{ $t('gallery.copyright') }}
          </span>
        </div>

        <v-card-actions class="px-8 pb-6 pt-2">
          <v-tooltip
            :text="$t('gallery.cropRequired')"
            location="top"
            :disabled="isVideo || isSound || cropConfirmed"
          >
            <template #activator="{ props: tooltipProps }">
              <span v-bind="tooltipProps">
                <v-btn
                  :disabled="!canConfirmUpload"
                  color="success"
                  variant="text"
                  icon
                  @click="uploadToServer"
                >
                  <v-icon size="42">mdi-check-circle</v-icon>
                </v-btn>
              </span>
            </template>
          </v-tooltip>
          <v-spacer></v-spacer>
          <v-btn
            v-if="!isSound"
            color="primary"
            variant="tonal"
            icon
            class="mx-2"
            @click="openCropEditor"
          >
            <v-icon size="24">mdi-crop</v-icon>
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="grey-darken-1" variant="text" icon @click="tryClose">
            <v-icon size="28">mdi-close</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
      <v-card v-if="showVideoCrop && media?.file" class="border-color">
        <v-card-title class="d-flex align-center justify-space-between pt-4 px-6">
          <span class="text-h6 font-weight-bold text-primary">{{
            $t('video.prepareTitle')
          }}</span>
          <v-btn icon variant="text" @click="showVideoCrop = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <iframe
          ref="compressorFrame"
          class="compressor-frame"
          :title="$t('video.prepareTitle')"
          src="/video-compressor/video-30mb-ffmpeg.html?embed=1"
        />
      </v-card>
      <v-card class="border-color" v-if="showCropDialog">
        <v-card-text class="pa-0">
          <cropper
            v-if="showCropDialog && modelValue"
            v-model="showCropDialog"
            @cropper-mounted="event => (cropperRef = event)"
            @crop-confirmed="onCropConfirmed"
            @closeUpload="mediaUploadVisible = false"
            :media="media"
            :aspect-ratio="aspectRatio"
            :cropped="cropped"
          />
        </v-card-text>
      </v-card>
    </div>
    <!-- {{ cropperRef }} -->
  </v-dialog>
</template>

<script>
import myAxios from '@/api/axios'
import { useMediaStore } from '@/store/media'
import { useSwal } from '@/composables/useSwal'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/store'
import {
  exceedsMp4Limit,
  isMp4File,
  isVideoFile,
  videoUploadAllowed,
} from '@/helpers/mp4Upload'

export default {
  props: {
    modelValue: {
      required: true,
    },
    imageFromInput: {
      type: Object,
      default: null,
    },
    // default: () => ({ url: null, name: null, mime: null }),
  },
  setup() {
    const { createSwal, timeoutSwal } = useSwal()
    const { t } = useI18n()
    return { createSwal, timeoutSwal, t }
  },
  data() {
    return {
      showCropDialog: false,
      aspectRatio: 1200 / 850,
      title: '',
      isTextfieldErrorState: false,
      loading: false,
      media: null,
      isVideo: false,
      isSound: false,
      cropperRef: {},
      showVideoCrop: false,
      videoPrepared: false,
      videRightSize: null,
      cropped: false,
      cropConfirmed: false,
    }
  },
  methods: {
    ...mapActions(useMediaStore, ['storeMedia']),
    videoTooLongHandler(value) {
      if (this.videoPrepared) {
        this.videRightSize = true
        return
      }
      if (value) {
        if (this.videRightSize === null) this.showVideoCrop = true
        this.videRightSize = false
      } else {
        this.videRightSize = true
      }
    },
    onCompressorMessage(event) {
      if (event.origin !== window.location.origin) return
      const frame = this.$refs.compressorFrame
      if (!frame || event.source !== frame.contentWindow) return
      if (event.data?.type === 'video-compressor-ready' && this.media?.file) {
        frame.contentWindow.postMessage(
          { type: 'video-compressor-file', file: this.media.file },
          window.location.origin
        )
        return
      }
      if (event.data?.type !== 'video-compressor-result') return
      const incoming = event.data.file
      if (!(incoming instanceof Blob)) return
      const file =
        incoming instanceof File
          ? incoming
          : new File([incoming], event.data.name || 'video.mp4', {
              type: 'video/mp4',
            })
      this.onVideoPrepared(file)
    },
    onVideoPrepared(file) {
      const previousUrl = this.media?.url
      this.videoPrepared = true
      this.videRightSize = true
      this.isVideo = true
      this.showVideoCrop = false
      this.media = {
        ...(this.media || {}),
        url: URL.createObjectURL(file),
        file,
        name: this.media?.name || file.name,
        type: 'video/mp4',
        mime: 'video/mp4',
      }
      if (
        previousUrl?.startsWith('blob:') &&
        previousUrl !== this.imageFromInput?.url
      ) {
        URL.revokeObjectURL(previousUrl)
      }
    },
    async tryClose() {
      if (this.media && !this.cropConfirmed) {
        const { isConfirmed } = await this.createSwal({
          icon: 'warning',
          title: this.media.isAI
            ? this.t('gallery.aiLeaveWarning')
            : this.t('questions.questionNotSaved'),
          confirmButtonText: this.t('generics.yes'),
          cancelButtonText: this.t('generics.no'),
        })
        if (!isConfirmed) return
      }
      this.close()
    },
    close() {
      this.media = null
      this.mediaUploadVisible = false
      this.videRightSize = null
      this.cropConfirmed = false
    },

    onCropConfirmed(event) {
      this.media = event
      this.cropConfirmed = true
      this.cropped = true
    },

    openCropEditor() {
      if (this.isVideo) {
        this.showVideoCrop = true
        return
      }
      this.showCropDialog = true
    },

    async uploadToServer() {
      try {
        if (!this.media?.name) {
          this.isTextfieldErrorState = true
          return
        } else {
          this.isTextfieldErrorState = false
        }
        if (
          !videoUploadAllowed(
            this.media.file,
            useAuthStore().hasPartnerFeature('video_upload')
          )
        ) {
          await this.createSwal({
            icon: 'error',
            title: this.t('gallery.videoPartnerOnly'),
          })
          return
        }
        if (isVideoFile(this.media.file) && !isMp4File(this.media.file)) {
          await this.createSwal({
            icon: 'error',
            title: this.t('gallery.videoMp4Only'),
          })
          return
        }
        if (exceedsMp4Limit(this.media.file)) {
          await this.createSwal({
            icon: 'error',
            title: this.t('gallery.videoTooLarge'),
          })
          return
        }
        this.loading = true
        const incoming = this.media.file?.size || 0
        if (
          this.maxBytes &&
          (incoming > this.maxBytes ||
            this.totalBytes + incoming > this.maxBytes)
        ) {
          this.loading = false
          this.timeoutSwal({
            icon: 'warning',
            title: this.t('gallery.storageExceeded'),
            timer: 4000,
          })
          return
        }

        const { data: newMedia } = await myAxios.postForm(
          '/upload/assets',
          {
            file: this.media.file,
          },
          { timeout: 180000 }
        )

        await this.storeMedia({
          url: newMedia.secure_url,
          bytes: newMedia.bytes,
          title: this.media.name,
          mime: this.media.mime,
        })
        this.media = null
        this.loading = false
        this.$emit('uploaded')
        this.mediaUploadVisible = false
      } catch (e) {
        this.loading = false
        const status = e?.response?.status
        const code = e?.response?.data?.code
        if (status === 403 && code === 'VIDEO_UPLOAD_DISABLED') {
          await this.createSwal({
            icon: 'error',
            title: this.t('gallery.videoPartnerOnly'),
          })
          return
        }
        if (status === 415 || code === 'ONLY_MP4') {
          await this.createSwal({
            icon: 'error',
            title: this.t('gallery.videoMp4Only'),
          })
          return
        }
        if (status === 413 && code === 'FILE_TOO_LARGE') {
          await this.createSwal({
            icon: 'error',
            title: this.t('gallery.videoTooLarge'),
          })
          return
        }
        if (status === 413) {
          this.timeoutSwal({
            icon: 'warning',
            title: this.t('gallery.storageExceeded'),
            timer: 4000,
          })
          return
        }
        this.mediaUploadVisible = false
        throw e
      }
    },
    onEnter(event) {
      if (event.key === 'Enter' && !this.showCropDialog) {
        this.uploadToServer()
      }
    },
  },
  computed: {
    ...mapState(useMediaStore, ['totalBytes', 'maxBytes']),
    errorMassage() {
      return this.isTextfieldErrorState ? this.$t('images.titleValidation') : ''
    },
    exceedsVideoLimit() {
      return exceedsMp4Limit(this.media?.file)
    },
    canConfirmUpload() {
      if (this.isVideo) {
        return this.videRightSize === true && !this.exceedsVideoLimit
      }
      if (this.isSound) return true
      return this.cropConfirmed
    },
    mediaUploadVisible: {
      get() {
        return this.modelValue
      },
      set(val) {
        this.$emit('update:modelValue', val)
      },
    },
  },
  watch: {
    showCropDialog(val, old) {
      if (!val && old && !this.cropped) {
        this.cropped = true
      }
    },
    imageFromInput(val) {
      this.videoPrepared = false
      this.videRightSize = null
      this.media = val
      if (val) {
        this.cropConfirmed = false
        this.cropped = false
      }
    },
    async media(val) {
      if (!val || this.cropConfirmed) return

      if (val.url.startsWith('data:')) {
        val.mime = val.url.split(':')[1].split(';')[0]
      } else if (val.type) {
        val.mime = val.type
      }

      const type = val.mime?.split('/')[0]

      if (type === 'image') {
        this.showCropDialog = true
      } else if (type === 'video') {
        this.isVideo = true
        if (String(val.url || '').startsWith('data:')) this.videoPrepared = true
        if (!this.videoPrepared) this.showVideoCrop = true
      } else if (type === 'audio') {
        this.isSound = true
      }
    },
  },
  mounted() {
    if (this.imageFromInput) this.media = this.imageFromInput
    window.addEventListener('keyup', this.onEnter)
    window.addEventListener('message', this.onCompressorMessage)
  },
  beforeUnmount() {
    window.removeEventListener('keyup', this.onEnter)
    window.removeEventListener('message', this.onCompressorMessage)
  },
}
</script>
<style scoped lang="scss">
:deep(.v-dialog) {
  box-shadow: unset !important;
}

.v-dialog :deep(.v-card.border-color) {
  border: 4px solid transparent;
  background:
    linear-gradient(#ffffff 0 0) padding-box,
    var(--linearHotPinkDodgerBlue) border-box;
  border-radius: 28px;
  overflow: hidden;
}

.media-preview-wrapper {
  width: 100%;
  max-width: 550px;
  border-radius: 24px;
  overflow: hidden;
  background-color: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.media-preview-wrapper:hover {
  transform: translateY(-4px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.18);
}

.copyright-notice {
  opacity: 0.7;
}

.custom-field :deep(.v-field__input) {
  padding-top: 8px;
  font-size: 1.1rem;
}

.compressor-frame {
  display: block;
  width: 100%;
  height: 640px;
  border: 0;
  background: transparent;
}

:deep(.cropper-bg) {
  background-color: none !important;
}
</style>
